/**
 * Juggernaut armour system — pure logic, no Phaser.
 *
 * Armour absorbs all HP damage until the pool is depleted. Heavy hits
 * (damage >= HEAVY_HIT_THRESHOLD) and throws drain armour at full value;
 * lighter hits chip at LIGHT_ARMOUR_FRACTION. High poise while armour
 * stands: hitstun is suppressed until the pool breaks.
 *
 * Separation contract: this module MUST NOT import Phaser.
 */

/** Damage value at or above which a hit counts as "heavy". */
export const HEAVY_HIT_THRESHOLD = 10;

/**
 * Fraction of damage applied to armour for light hits
 * (damage < HEAVY_HIT_THRESHOLD, non-throw).
 */
export const LIGHT_ARMOUR_FRACTION = 0.25;

export interface JuggernautArmourState {
  armour: number;
  readonly maxArmour: number;
  isBroken: boolean;
}

export function initialArmourState(maxArmour: number): JuggernautArmourState {
  return { armour: maxArmour, maxArmour, isBroken: false };
}

export interface ArmourHitResult {
  /** Armour points consumed by this hit. */
  armourDamage: number;
  /** HP damage to apply (0 while armour stands). */
  hpDamage: number;
  /** Hitstun frames to apply (0 while armour stands — high poise). */
  hitstunFrames: number;
  /** True when this hit was the killing blow to the armour pool. */
  justBroke: boolean;
}

/**
 * Apply a hit against the armour layer. Mutates `state.armour` and `state.isBroken`.
 *
 * While armour stands:  HP damage = 0, hitstun = 0 (high poise).
 * Once armour is gone:  full HP damage + hitstun pass through.
 *
 * @param state    Mutable armour state for this entity.
 * @param spec     Incoming hit parameters (damage + hitstun).
 * @param isThrow  True when the hit originates from a thrown combatant — always heavy.
 */
export function applyHitToArmour(
  state: JuggernautArmourState,
  spec: { damage: number; hitstun: number },
  isThrow = false,
): ArmourHitResult {
  if (state.isBroken) {
    return {
      armourDamage: 0,
      hpDamage: spec.damage,
      hitstunFrames: spec.hitstun,
      justBroke: false,
    };
  }

  const isHeavy = isThrow || spec.damage >= HEAVY_HIT_THRESHOLD;
  const armourDamage = isHeavy
    ? spec.damage
    : Math.max(1, Math.round(spec.damage * LIGHT_ARMOUR_FRACTION));

  const prevArmour = state.armour;
  state.armour = Math.max(0, state.armour - armourDamage);
  const justBroke = prevArmour > 0 && state.armour === 0;
  if (justBroke) {
    state.isBroken = true;
  }

  return {
    armourDamage,
    hpDamage: 0,
    hitstunFrames: 0,
    justBroke,
  };
}

/** Armour fraction remaining (0–1). Used for visual feedback. */
export function armourFraction(state: JuggernautArmourState): number {
  return state.maxArmour > 0 ? state.armour / state.maxArmour : 0;
}
